import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

import common from '@/shared/i18n/en/common';
import messages from '@/shared/i18n/en/pwa';
import { useT } from '@/shared/i18n/react';

import { usePwaUpdate } from './pwa-update.store';

/**
 * Persistent "a new version is ready" bar.
 *
 * Rendered once at the app root. Stays hidden until the service worker reports
 * a waiting update (`register-sw.ts` → `usePwaUpdate.announceUpdate`), then pins
 * a full-width bar to the very top of the viewport — above every shell, clear of
 * the mobile bottom-nav, and padded for the iOS status-bar / notch inset so it's
 * unmissable in a home-screen PWA. Tapping "Update now" activates the new worker
 * and reloads onto the fresh bundle.
 *
 * It is intentionally NOT dismissible: the whole point is that the user reliably
 * sees the prompt and takes the one tap needed to get the new version.
 */
export function PwaUpdateBanner() {
  const updateAvailable = usePwaUpdate((s) => s.updateAvailable);
  const applyUpdate = usePwaUpdate((s) => s.applyUpdate);
  const [reloading, setReloading] = useState(false);
  const t = useT(messages);
  const tc = useT(common);

  if (!updateAvailable) return null;

  return (
    <div
      role="alert"
      className="fixed inset-x-0 top-0 z-[200] bg-[var(--accent)] text-white shadow-[0_2px_12px_rgba(0,0,0,0.35)] pt-[env(safe-area-inset-top)]"
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <RefreshCw className="size-4 shrink-0" aria-hidden />
        <span className="flex-1 text-caption font-medium leading-snug">
          {t('updateReady')}
        </span>
        <button
          type="button"
          onClick={() => {
            if (reloading) return;
            setReloading(true);
            applyUpdate?.();
          }}
          disabled={reloading}
          className="shrink-0 h-9 px-4 rounded-lg text-caption font-semibold bg-white/95 text-[var(--accent)] active:scale-[0.97] transition-transform disabled:opacity-70"
        >
          {reloading ? tc('updating') : t('updateNow')}
        </button>
      </div>
    </div>
  );
}
